/**
 * Hook per gestire le notifiche del forum
 * Fornisce conteggio notifiche non lette e funzionalità di lettura
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";

export interface ForumNotification {
  id: string;
  user_id: string;
  topic_id: string | null;
  reply_id: string | null;
  notification_type: "new_topic" | "new_reply" | "mention" | "solution_marked";
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export const useForumNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query per il conteggio notifiche non lette
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["forum-notifications-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from("forum_notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refresh ogni 30 secondi
  });

  // Query per le notifiche complete
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["forum-notifications", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("forum_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as ForumNotification[];
    },
    enabled: !!user,
  });

  // Mutation per segnare come letta
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("forum_notifications")
        .update({ is_read: true })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["forum-notifications-count", user?.id] });
    },
  });

  // Mutation per segnare tutte come lette
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from("forum_notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["forum-notifications-count", user?.id] });
    },
  });

  // Realtime subscription per nuove notifiche
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`forum-notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "forum_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["forum-notifications", user.id] });
          queryClient.invalidateQueries({ queryKey: ["forum-notifications-count", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
};
